import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import {
  getAllAssignmentsService,
  createAssignmentService,
  updateAssignmentService,
  deleteAssignmentService,
  getAssignmentService,
} from "@/service/assignment.service";
import {
  AssignmentFormData,
  AssignmentModel,
  AssignmentQuery,
} from "@/types/assignment.model";
import type { AxiosError } from "axios";

interface AssignmentState {
  assignments: AssignmentModel[];
  currentAssignment: AssignmentFormData | null;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  totalItems: number;
  noAssignments: boolean;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
}

const initialState: AssignmentState = {
  assignments: [],
  noAssignments: true,
  currentAssignment: null,
  loading: false,
  actionLoading: false,
  error: null,
  totalItems: 0,
  pagination: {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  },
};

// Async Actions
export const fetchAssignments = createAsyncThunk(
  "assignment/fetchAll",
  async (query: AssignmentQuery, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));

      const state = getState() as RootState;

      const { teacher_id, school_id } = state.auth.user || {};

      if (teacher_id) {
        query.teacherId = teacher_id;
      }

      if (school_id) {
        query.schoolId = school_id;
      }

      const {
        data: { data, pageInfo },
      } = await getAllAssignmentsService(query);
      dispatch(setLoading(false));

      dispatch(setNoAssignments(false));

      console.log({ data });

      return {
        assignments: data,
        // Handle different API response formats for pagination
        pagination: {
          totalItems: pageInfo?.total_count as number,
          totalPages: pageInfo?.total_pages as number,
          currentPage: pageInfo?.current_page as number,
          pageSize: pageInfo?.per_page as number,
        },
      };
    } catch (error: any) {
      if (error.response?.status === 404) {
        dispatch(setNoAssignments(true));
        throw error;
      }

      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch assignments";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const fetchAssignmentById = createAsyncThunk(
  "assignment/fetchById",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      const response = await getAssignmentService(id);
      dispatch(setActionLoading(false));
      if (response && response.data) {
        return response.data.data;
      }
      throw new Error("Invalid response format");
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to fetch assignment";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const createAssignment = createAsyncThunk(
  "assignment/create",
  async (
    {
      assignmentData,
      navigate,
    }: { assignmentData: Partial<AssignmentFormData>; navigate: any },
    { dispatch }
  ) => {
    try {
      dispatch(setActionLoading(true));
      const {
        data: { data },
      } = await createAssignmentService(assignmentData as AssignmentFormData);
      dispatch(setActionLoading(false));
      navigate("/assignments");
      return data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to create assignment";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const updateAssignment = createAsyncThunk(
  "assignment/update",
  async (
    {
      id,
      assignmentData,
    }: { id: string; assignmentData: Partial<AssignmentFormData> },
    { dispatch }
  ) => {
    try {
      dispatch(setActionLoading(true));
      // Ensure the assignment object has the id property
      const assignmentToUpdate = { ...assignmentData, id };
      const response = await updateAssignmentService(
        id,
        assignmentToUpdate as AssignmentFormData & AssignmentModel
      );
      dispatch(setActionLoading(false));

      return response.data.data;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to update assignment";
      dispatch(setError(message));
      throw error;
    }
  }
);

export const deleteAssignment = createAsyncThunk(
  "assignment/delete",
  async (id: string, { dispatch }) => {
    try {
      dispatch(setActionLoading(true));
      await deleteAssignmentService(id);
      dispatch(setActionLoading(false));

      dispatch(
        fetchAssignments({
          page: 1,
          pageSize: 10,
          search: "",
          fromDate: undefined,
          toDate: undefined,
          type: "general",
        })
      );

      return id;
    } catch (error) {
      const axiosError = error as AxiosError<{ message: string }>;
      const message =
        axiosError.response?.data?.message || "Failed to delete assignment";
      dispatch(setError(message));
      throw error;
    }
  }
);

const assignmentSlice = createSlice({
  name: "assignment",
  initialState,
  reducers: {
    setNoAssignments: (state, action: PayloadAction<boolean>) => {
      state.noAssignments = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setActionLoading: (state, action: PayloadAction<boolean>) => {
      state.actionLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
      state.actionLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetCurrentAssignment: (state) => {
      state.currentAssignment = null;
    },
    resetAllState: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // Fetch all assignments
      .addCase(fetchAssignments.fulfilled, (state, action) => {
        state.assignments = action.payload.assignments;
        state.pagination = action.payload.pagination;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAssignments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assignments";
      })

      // Fetch assignment by id
      .addCase(fetchAssignmentById.fulfilled, (state, action) => {
        state.currentAssignment = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(fetchAssignmentById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch assignment";
      })

      // Create assignment
      .addCase(createAssignment.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(createAssignment.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to create assignment";
      })

      // Update assignment
      .addCase(updateAssignment.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(updateAssignment.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to update assignment";
      })

      // Delete assignment
      .addCase(deleteAssignment.fulfilled, (state) => {
        state.actionLoading = false;
        state.error = null;
      })
      .addCase(deleteAssignment.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.error.message || "Failed to delete assignment";
      });
  },
});

export const {
  setLoading,
  setActionLoading,
  setError,
  clearError,
  resetCurrentAssignment,
  resetAllState,
  setNoAssignments,
} = assignmentSlice.actions;

// Selectors
export const selectAssignments = (state: RootState) =>
  state.assignments.assignments;
export const selectAssignmentLoading = (state: RootState) =>
  state.assignments.loading;
export const selectAssignmentActionLoading = (state: RootState) =>
  state.assignments.actionLoading;
export const selectAssignmentError = (state: RootState) =>
  state.assignments.error;

// Additional selectors
export const selectAllAssignments = (state: RootState) =>
  state.assignments.assignments;
export const selectAssignmentById = (state: RootState, id: string) =>
  state.assignments.assignments.find((assignment) => assignment.id === id);
export const selectTotalItems = (state: RootState) =>
  state.assignments.pagination.totalItems;
export const selectNoAssignments = (state: RootState) =>
  state.assignments.noAssignments;

export default assignmentSlice.reducer;
